/** @jsx jsx */

import React, {
  Fragment as _,
  memo,
  useMemo,
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { Box } from 'theme-ui';
import { keyframes } from 'emotion';
import { css, jsx } from '@emotion/core';
import { useSelector, useDispatch, connect } from 'react-redux';
import { colors } from 'styles/variables';
import { EditWrapper0, EditWrapper1 } from './EditWrappers';
import LoadingSpinner from 'components/UI/LoadingSpinner';
import FullScreenIcon from 'assets/FullScreenIcon.svg';
import isAndroidSamsung from 'services/isAndroidSamsung';
import isiOS from 'services/isiOS';
import CloseFullscreenEmbed from 'assets/CloseFullscreenEmbed.svg';
import { hideHeaderAction } from 'actions/hideHeaderAction';

const { lPink, lGrey0, lGrey2 } = colors;

const frameSelected = keyframes`
  from {
    width: 50%;
    height: 50%;
  }
  to {
    width: 75%;
    height: 75%;
  }
`;

const minHeight = '50vh';
const getMinHeight = fullWindowHeight => {
  return fullWindowHeight ? 'calc(100vh - 250px)' : minHeight;
};

const IFrame = ({
  src,
  fullWindowHeight,
  fullscreenMode = false,
}: {
  src: string;
  fullWindowHeight?: string;
  fullscreenMode?: boolean | undefined;
}) => {
  const [loaded, setLoaded] = useState(false);

  // console.log('fullscreenMode :', fullscreenMode);

  return (
    <_>
      <iframe
        src={src || 'https://www.chameleoncreator.com'}
        css={css`
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          ${loaded && `min-height: ${getMinHeight(fullWindowHeight)};`}
          border: none;

          ${!loaded &&
            `
            width: 0;
            height: 0;
          `}

          ${fullscreenMode &&
            `
            width: 100vw;
            height: 100vh;
          `}
        `}
        onLoad={(e: any) => {
          setLoaded(true);
        }}
      ></iframe>
      {!loaded && (
        <div
          css={css({
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            // width: fullscreenMode ? '100vw' : '100%',
            // height: fullscreenMode ? '100vh' : '100%',
            width: '100%',
            height: '100%',
            minHeight: getMinHeight(fullWindowHeight),
          })}
        >
          <LoadingSpinner />
        </div>
      )}
    </_>
  );
};

const Embed = props => {
  const { id, src, fullWindowHeight = false, mediaParentId = null } = props;

  const [iFrameHovered, setIFrameHovered] = useState<boolean>(false);
  const [fullscreenMode, setFullscreenMode] = useState<boolean>(false);

  const focusedNodeId = useSelector((state: any) => state.focusedNodeId);
  const dispatch = useDispatch();

  const selected = id === focusedNodeId;

  //
  const allowFullScreen = window.dev && !window.dev() && props.allowFullScreen;
  // alert(`allowFullScreen : ${allowFullScreen}`);

  const hideHeader = (hide: boolean) => {
    // dispatch({
    //   type: 'HIDE_HEADER',
    //   data: {
    //     hide,
    //   },
    // });
    dispatch(hideHeaderAction(hide));
  };

  useEffect(() => {
    if (
      isAndroidSamsung() ||
      isiOS() ||
      window.matchMedia('(hover: none)').matches
    ) {
      // hover unavailable
      setIFrameHovered(true);
    } else {
      // console.log('HOVER IS AVAILABLE');
    }
    //
  }, []);

  useEffect(() => {
    const exitFullscreen = e => {
      if (e.key === 'Escape') {
        hideHeader(false);
        document.removeEventListener('keydown', exitFullscreen);
        setFullscreenMode(false);
      }
    };

    if (fullscreenMode) {
      hideHeader(true);
      document.addEventListener('keydown', exitFullscreen);
    } else {
      hideHeader(false);
      document.removeEventListener('keydown', exitFullscreen);
      // fullscreenContainer.current && fullscreenContainer.current.remove();
    }

    return () => {
      //
    };
  }, [fullscreenMode]);

  return (
    <div
      css={css({
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: getMinHeight(fullWindowHeight),
        outline: `1px solid ${lGrey2}`,
        boxShadow: '5px 10px 20px 10px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',

        ...(fullscreenMode && {
          position: 'fixed',
          left: 0,
          top: 0,
          width: '100vw',
          height: '100vh',
          background: '#fff',
          zIndex: 2147483647,
        }),
      })}
      onMouseOver={() => {
        setIFrameHovered(true);
      }}
      onMouseOut={() => {
        setIFrameHovered(false);
      }}
    >
      {window.dev() ? (
        <EditWrapper1 id={id} mediaParentId={mediaParentId} selected={selected}>
          <IFrame src={src} fullWindowHeight={fullWindowHeight} />
        </EditWrapper1>
      ) : (
        <IFrame
          src={src}
          fullWindowHeight={fullWindowHeight}
          fullscreenMode={fullscreenMode}
        />
      )}

      {!!allowFullScreen &&
        (!fullscreenMode ? (
          <button
            css={css({
              border: 'none',
              outline: 'none',
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              left: 0,
              bottom: '0px',
              width: '100%',
              height: 55,
              padding: 15,
              background: `rgba(7, 9, 17, ${iFrameHovered ? 0.7 : 0})`,
              transform: `translateY(${iFrameHovered ? 0 : '55px'})`,
              transition: 'transform 0.25s ease-out',
              color: '#fff',
              cursor: 'pointer',
            })}
            onClick={() => {
              setFullscreenMode(true);
            }}
          >
            <img src={FullScreenIcon} style={{ margin: '0 15px 0 0' }} />
            Full screen
          </button>
        ) : (
          <button
            css={css({
              border: 'none',
              outline: 'none',
              position: 'absolute',
              right: 35,
              top: 20,
              width: 35,
              height: 35,
              background: `url('${CloseFullscreenEmbed}') no-repeat`,
              cursor: 'pointer',

              '&:hover': {
                opacity: 0.5,
              },
            })}
            onClick={() => {
              setFullscreenMode(false);
            }}
          />
        ))}
    </div>
  );
};

export default Embed;
